import type { TermsDoc, TermsNode } from '@zuruflex/types';
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * A partner's terms, drawn from the stored ProseMirror JSON.
 *
 * Not `dangerouslySetInnerHTML` over rendered markup, and not a markdown
 * library: the document is a node tree, so it is walked into elements. The
 * server allow-lists every node and mark it may contain, which means this
 * component never meets a type it does not have a case for — and an
 * unknown one is skipped rather than rendered, so a document written
 * against a newer schema degrades to missing a clause rather than to a
 * blank page.
 *
 * Deliberately untranslated. Clauses are content drafted per jurisdiction,
 * not copy — the rule `docs/I18N.md` already sets for the policy pages.
 */
export interface TermsDocumentProps {
  doc: TermsDoc;
  className?: string;
}

export function TermsDocument({ doc, className }: TermsDocumentProps) {
  return (
    <div className={cn('space-y-4 text-sm leading-relaxed text-text-default', className)}>
      {(doc.content ?? []).map((node, index) => (
        <Block key={index} node={node} />
      ))}
    </div>
  );
}

function Block({ node }: { node: TermsNode }) {
  switch (node.type) {
    case 'heading':
      return node.attrs?.level === 3 ? (
        <h3 className="pt-2 text-sm font-bold text-text-default">
          <Inline content={node.content} />
        </h3>
      ) : (
        <h2 className="pt-4 text-base font-bold text-text-default first:pt-0">
          <Inline content={node.content} />
        </h2>
      );
    case 'paragraph':
      return (
        <p>
          <Inline content={node.content} />
        </p>
      );
    case 'bulletList':
      return (
        <ul className="list-disc space-y-1 pl-5 marker:text-text-muted">
          <Items content={node.content} />
        </ul>
      );
    case 'orderedList':
      return (
        <ol className="list-decimal space-y-1 pl-5 marker:text-text-muted">
          <Items content={node.content} />
        </ol>
      );
    default:
      return null;
  }
}

function Items({ content }: { content?: TermsNode[] }) {
  return (
    <>
      {(content ?? []).map((item, index) => (
        <li key={index} className="space-y-2">
          {(item.content ?? []).map((child, childIndex) => (
            <Block key={childIndex} node={child} />
          ))}
        </li>
      ))}
    </>
  );
}

function Inline({ content }: { content?: TermsNode[] }): ReactNode {
  return (
    <>
      {(content ?? []).map((node, index) => {
        if (node.type === 'hardBreak') return <br key={index} />;
        if (node.type !== 'text') return null;

        let rendered: ReactNode = node.text;
        const href = node.marks?.find((mark) => mark.type === 'link')?.attrs?.href;

        if (node.marks?.some((mark) => mark.type === 'bold')) {
          rendered = <strong className="font-semibold">{rendered}</strong>;
        }
        if (node.marks?.some((mark) => mark.type === 'italic')) {
          rendered = <em>{rendered}</em>;
        }
        if (href) {
          rendered = (
            /*
             * `noopener` because the href came from a partner. The schema
             * already refuses anything but an absolute http(s) URL, so this
             * is the second lock rather than the only one.
             */
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline underline-offset-2"
            >
              {rendered}
            </a>
          );
        }
        return <span key={index}>{rendered}</span>;
      })}
    </>
  );
}

/**
 * Zuruflex's own policy documents, which are a different shape.
 *
 * `LegalDocument` in `@zuruflex/core` is authored in code as headings,
 * paragraphs and definition rows — a contract drafted by a lawyer, not
 * typed into an editor — so it does not go through the ProseMirror path
 * above. Rendering it here as well means the partner agreement looks the
 * same on the website that publishes it and on the gate that asks a
 * partner to accept it.
 */
export interface LegalSectionsProps {
  sections: {
    heading: string;
    body: string[];
    rows?: { term: string; detail: string }[];
  }[];
  className?: string;
}

export function LegalSections({ sections, className }: LegalSectionsProps) {
  return (
    <div className={cn('space-y-8 text-sm leading-relaxed text-text-default', className)}>
      {sections.map((section) => (
        <section key={section.heading} className="space-y-3">
          <h2 className="text-base font-bold text-text-default">{section.heading}</h2>
          {section.body.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
          {section.rows?.length ? (
            <dl className="mt-3 divide-y divide-border-default rounded-xl border border-border-default">
              {section.rows.map((row) => (
                <div key={row.term} className="grid gap-1 p-3 sm:grid-cols-[10rem_1fr] sm:gap-4">
                  <dt className="font-semibold text-text-default">{row.term}</dt>
                  <dd className="text-text-muted">{row.detail}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </section>
      ))}
    </div>
  );
}
